import { useState, useEffect } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { createServerSupabaseClient, getSessionFromSSR, getCurrentUserFromSSR, hasBusinessOrAdminPermission } from '@/services/supabase';

interface ProfileForm {
  name: string;
  email: string;
  role: string;
  phone?: string;
  address?: string;
}

// Server-side function that can be used with Next.js getServerSideProps
export async function getProfileSettingsDataSSR(context: any) {
  try {
    // Create a server-side Supabase client
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the session using the SSR method
    const session = await getSessionFromSSR(supabaseClient);
    
    // If no session, return null
    if (!session) {
      return { user: null, data: null };
    }
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions using the centralized function
    if (!hasBusinessOrAdminPermission(user)) {
      return { user: null, data: null };
    }
    
    // In a real implementation, we might fetch additional profile data
    // For now, we'll just return the user
    return {
      user,
      data: null
    };
  } catch (error) {
    console.error('Error in getProfileSettingsDataSSR:', error);
    return { user: null, data: null };
  }
}

// Props interface for ProfileSettings component to accept SSR data
interface ProfileSettingsProps {
  initialUser?: any;
  initialData?: any;
}

export default function ProfileSettings({ initialUser }: ProfileSettingsProps = {}) {
  const { user, logout } = useAuthV2();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<ProfileForm>({
    name: '',
    email: '',
    role: '',
    phone: '',
    address: '',
  });

  useEffect(() => {
    // If we have initialUser from SSR, use it directly
    if (initialUser) {
      // Safely access properties that might not exist on all user types
      const userData = {
        name: initialUser.name || '',
        email: initialUser.email || '',
        role: initialUser.role || '',
        phone: '',
        address: ''
      };
      
      // Add additional properties that might be in user metadata
      if ('phone' in initialUser) {
        userData.phone = (initialUser as any).phone || '';
      }
      
      if ('address' in initialUser) {
        userData.address = (initialUser as any).address || '';
      }
      
      // Set the form data
      setFormData(userData);
    } else if (user) {
      // Otherwise use the existing client-side method
      // Safely access properties that might not exist on all user types
      const userData = {
        name: user.name || '',
        email: user.email || '',
        role: user.role || '',
        phone: '',
        address: ''
      };
      
      // Add additional properties that might be in user metadata
      if ('phone' in user) {
        userData.phone = (user as any).phone || '';
      }
      
      if ('address' in user) {
        userData.address = (user as any).address || '';
      }
      
      // Set the form data
      setFormData(userData);
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // In a real app, this would make an API call to update the user's profile
      // await updateUserProfile(formData);
      
      // Mock update success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Layout title="">
      <div className="space-y-6">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="mr-4 text-gray-500 hover:text-navy-blue"
        >
        </button>
        <h1 className="text-xl font-medium text-navy-blue">Your Profile</h1>
      </div>

      {saveSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">Profile updated successfully!</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-navy-blue">Personal Information</h2>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled
                    className="shadow-sm bg-gray-50 block w-full sm:text-sm border-gray-300 rounded-md cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="role"
                    id="role"
                    value={formData.role}
                    disabled
                    className="shadow-sm bg-gray-50 block w-full sm:text-sm border-gray-300 rounded-md cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Contact admin to change role</p>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="sm:col-span-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
              >
                Sign out
              </button>
              
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </Layout>
  );
}
